import React from 'react';
import { cn } from '@/lib/utils';
import { HtmlContent } from '@/components/ui/html-content';
import { hasValidHTMLContent } from '@/utils/html-content';
import { useReportI18n } from '@/pages/reports/i18n';

export type CuratorshipPresentation = 'list' | 'blocks';

interface CuratorshipCardItem {
  id?: string;
  title: string;
  url: string;
  description?: string;
}

interface CuratorshipCardProps {
  sectionTitle?: string;
  intro?: string;
  presentation?: CuratorshipPresentation;
  items?: CuratorshipCardItem[];
  className?: string;
}

function normalizeHref(raw: string): string {
  const t = (raw ?? '').trim();
  if (!t) return '#';
  if (/^www\./i.test(t)) return `https://${t}`;
  return t;
}

function isFilledItem(item: CuratorshipCardItem): boolean {
  return Boolean(item.title?.trim() && item.url?.trim());
}

function ItemLine({ item }: { item: CuratorshipCardItem }) {
  const href = normalizeHref(item.url);
  return (
    <span className="text-black font-['Times_New_Roman'] text-base md:text-lg leading-relaxed">
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="font-bold underline text-black"
      >
        {item.title.trim()}
      </a>
      {item.description?.trim() ? (
        <span className="font-normal">: {item.description.trim()}</span>
      ) : null}
    </span>
  );
}

export const CuratorshipCard: React.FC<CuratorshipCardProps> = ({
  sectionTitle,
  intro,
  presentation = 'list',
  items = [],
  className,
}) => {
  const { t } = useReportI18n();
  const filled = items.filter(isFilledItem);
  const heading = (sectionTitle ?? '').trim() || t('curatorship.defaultTitle');
  const showIntro = Boolean(intro && hasValidHTMLContent(intro));
  const hasItems = filled.length > 0;

  if (!showIntro && !hasItems) {
    return null;
  }

  const useList = presentation !== 'blocks';

  return (
    <section className={cn('w-full py-10 md:py-14 px-4', className)}>
      <div className="container mx-auto max-w-[560px]">
        <div className="bg-[#f0f0ee] px-6 py-8 md:px-10 md:py-10 shadow-sm">
          <h2 className="font-['Times_New_Roman'] font-bold text-2xl md:text-3xl text-black leading-tight mb-6">
            {heading}
          </h2>
          {showIntro && (
            <div className="mb-6 text-black font-['Times_New_Roman'] text-base md:text-lg">
              <HtmlContent html={intro!} className="[&_p]:mb-3 [&_p:last-child]:mb-0" />
            </div>
          )}
          {hasItems && (
            useList ? (
              <ul className="list-disc pl-5 space-y-3 marker:text-black">
                {filled.map((item) => (
                  <li key={item.id ?? `${item.title}-${item.url}`}>
                    <ItemLine item={item} />
                  </li>
                ))}
              </ul>
            ) : (
              <div className="space-y-4">
                {filled.map((item) => (
                  <p key={item.id ?? `${item.title}-${item.url}`} className="m-0">
                    <ItemLine item={item} />
                  </p>
                ))}
              </div>
            )
          )}
        </div>
      </div>
    </section>
  );
};
